"use client"

import { useEffect, useRef, useCallback, useState, useMemo } from "react"
import type { MapState, MapNode, EventType, NodeType } from "@/types/map.types"
import { getAvailableNodes, isNodeAccessible } from "@/systems/map-generation"
import { SpaceMapNode } from "./space-map-node"
import { SpaceMapConnections } from "./space-map-connections"
import { levels } from "@/data/levels"
import { useViewport } from "@/contexts/viewport-context"
import { cn } from "@/lib/utils"
import { enemyTypes } from "@/data/enemy-types"
import type { AttackType as DamageType, DefenseType } from "@/types/hero.types"
import type { LevelConfig } from "@/types/game.types"
import { itemDefinitions } from "@/data/item-types"
import { ItemRewardCard } from "@/components/ui/item-card"
import { BookUp } from "lucide-react"
import { playSpaceshipEngineSound } from "@/hooks/use-sound-effects"

// Spaceship travel animation duration in ms
const TRAVEL_DURATION = 1500
// Delay before starting combat/boss levels
const COMBAT_START_DELAY = 3000

// Helper to get dominant armor and damage types for a level
function getLevelCombatInfo(level: LevelConfig): { dominantArmor: DefenseType; dominantDamage: DamageType } {
  const armorCounts: Record<DefenseType, number> = { unarmored: 0, light: 0, medium: 0, heavy: 0, fortified: 0, hero: 0, divine: 0 }
  const damageCounts: Record<DamageType, number> = { normal: 0, pierce: 0, magic: 0, chaos: 0, siege: 0, hero: 0, spells: 0 }

  for (const wave of level.waves) {
    for (const enemy of wave.enemies) {
      // Extract base enemy type (remove :elite/:unique suffix)
      const baseType = enemy.type.split(":")[0]
      const enemyConfig = enemyTypes[baseType]
      if (enemyConfig) {
        const weight = enemy.count
        armorCounts[enemyConfig.stats.defenseType] += weight
        damageCounts[enemyConfig.stats.damageType] += weight
      }
    }
  }

  // Find dominant types
  let dominantArmor: DefenseType = "light"
  let maxArmorCount = 0
  for (const [armor, count] of Object.entries(armorCounts)) {
    if (count > maxArmorCount) {
      maxArmorCount = count
      dominantArmor = armor as DefenseType
    }
  }

  let dominantDamage: DamageType = "normal"
  let maxDamageCount = 0
  for (const [damage, count] of Object.entries(damageCounts)) {
    if (count > maxDamageCount) {
      maxDamageCount = count
      dominantDamage = damage as DamageType
    }
  }

  return { dominantArmor, dominantDamage }
}

// Armor and damage type display info
const armorInfo: Record<DefenseType, { label: string; color: string }> = {
  unarmored: { label: "Unarmored", color: "text-gray-300" },
  light: { label: "Light", color: "text-green-400" },
  medium: { label: "Medium", color: "text-yellow-400" },
  heavy: { label: "Heavy", color: "text-orange-400" },
  fortified: { label: "Fortified", color: "text-red-400" },
  hero: { label: "Hero", color: "text-purple-400" },
  divine: { label: "Divine", color: "text-amber-400" },
}

const damageInfo: Record<DamageType, { label: string; color: string }> = {
  normal: { label: "Physical", color: "text-gray-300" },
  pierce: { label: "Pierce", color: "text-teal-400" },
  magic: { label: "Magic", color: "text-blue-400" },
  chaos: { label: "Chaos", color: "text-purple-400" },
  siege: { label: "Siege", color: "text-orange-400" },
  hero: { label: "Hero", color: "text-purple-400" },
  spells: { label: "Spells", color: "text-sky-400" },
}

// Get relative difficulty label based on node row position
function getDifficultyInfo(row: number, totalRows: number, nodeType: NodeType): { label: string; color: string } {
  // Boss nodes are always "Extreme"
  if (nodeType === "boss") {
    return { label: "Extreme", color: "text-red-500" }
  }

  // Calculate difficulty percentage (0 = start, 1 = end)
  const progress = row / Math.max(1, totalRows - 1)

  if (progress <= 0.2) {
    return { label: "Easy", color: "text-green-400" }
  } else if (progress <= 0.4) {
    return { label: "Moderate", color: "text-yellow-400" }
  } else if (progress <= 0.6) {
    return { label: "Challenging", color: "text-orange-400" }
  } else if (progress <= 0.8) {
    return { label: "Hard", color: "text-red-400" }
  } else {
    return { label: "Very Hard", color: "text-red-500" }
  }
}

// Zoom constants for star chart
const ZOOM_MIN = 0.5
const ZOOM_MAX_MOBILE = 2
const ZOOM_DEFAULT = 1
const ZOOM_PADDING = 160 // Top + bottom padding for max zoom calculation

interface SpaceMapViewProps {
  mapState: MapState
  onNodeSelect: (node: MapNode) => void
  onNodeArrival?: (node: MapNode) => void  // Called when spaceship arrives at node (before level starts)
  onRevealEvent: (nodeId: string) => { revealed: boolean; eventType?: EventType; itemId?: string }
  onCollectPelf: (nodeId: string) => { added: boolean; overflow: boolean; itemId?: string }
  onCompleteShopNode?: () => void  // Called when leaving a shop node to mark it complete
  onOpenUpgradeTree?: () => void
  onClose: () => void
}

export function SpaceMapView({ mapState, onNodeSelect, onNodeArrival, onRevealEvent, onCollectPelf, onCompleteShopNode, onOpenUpgradeTree, onClose }: SpaceMapViewProps) {
  const { isDesktop } = useViewport()
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const mapContentRef = useRef<HTMLDivElement>(null)
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)

  // Pelf reward state - tracks revealed pelf rewards that haven't been collected yet
  const [pelfRewardState, setPelfRewardState] = useState<{
    nodeId: string
    itemId: string
    collected: boolean
    inventoryFull: boolean
  } | null>(null)

  // Zoom state
  const [zoom, setZoom] = useState(ZOOM_DEFAULT)

  // Spaceship travel animation state
  const [spaceshipPosition, setSpaceshipPosition] = useState<{ x: number; y: number } | null>(null)
  const [isSpaceshipTraveling, setIsSpaceshipTraveling] = useState(false)
  const [isSpaceshipOrbiting, setIsSpaceshipOrbiting] = useState(true)
  const [isWaitingToStart, setIsWaitingToStart] = useState(false)
  const [launchProgress, setLaunchProgress] = useState(0)
  const [shopEventReady, setShopEventReady] = useState(false) // True when spaceship arrived at shop event
  const travelAnimationRef = useRef<number | null>(null)
  const launchAnimationRef = useRef<number | null>(null)
  const pendingNodeSelectRef = useRef<{ node: MapNode; timeout: NodeJS.Timeout } | null>(null)

  // Pinch-to-zoom refs
  const pinchStartDistRef = useRef<number>(0)
  const pinchStartZoomRef = useRef<number>(ZOOM_DEFAULT)

  // Drag-to-pan refs
  const isDraggingRef = useRef<boolean>(false)
  const dragStartRef = useRef<{ x: number; y: number; scrollX: number; scrollY: number } | null>(null)

  const availableNodes = getAvailableNodes(mapState)
  const availableNodeIds = availableNodes.map(n => n.id)

  // Get current node for spaceship position
  const currentNode = useMemo(() => {
    if (mapState.currentNodeId) {
      return mapState.nodes.find(n => n.id === mapState.currentNodeId)
    }
    // Fallback to last visited node
    if (mapState.visitedNodeIds.length > 0) {
      const lastVisitedId = mapState.visitedNodeIds[mapState.visitedNodeIds.length - 1]
      return mapState.nodes.find(n => n.id === lastVisitedId)
    }
    // Fallback to first available node (starting position)
    return availableNodes[0]
  }, [mapState.currentNodeId, mapState.visitedNodeIds, mapState.nodes, availableNodes])

  // Initialize spaceship position when map opens or current node changes
  useEffect(() => {
    if (currentNode && !isSpaceshipTraveling) {
      setSpaceshipPosition({ x: currentNode.position.x, y: currentNode.position.y })
      setIsSpaceshipOrbiting(true)
    }
  }, [currentNode?.id, isSpaceshipTraveling])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (travelAnimationRef.current) {
        cancelAnimationFrame(travelAnimationRef.current)
      }
      if (pendingNodeSelectRef.current) {
        clearTimeout(pendingNodeSelectRef.current.timeout)
      }
    }
  }, [])

  // Calculate content height based on total rows
  const contentHeight = mapState.totalRows * 90 + 120

  // Calculate dynamic max zoom - on desktop, limit so content fits in viewport with padding
  const maxZoom = useMemo(() => {
    if (!isDesktop) return ZOOM_MAX_MOBILE
    // Calculate max zoom so content height * zoom <= viewport height - padding
    const viewportHeight = typeof window !== "undefined" ? window.innerHeight : 800
    const maxZoomForHeight = (viewportHeight - ZOOM_PADDING) / contentHeight
    // Clamp between reasonable values
    return Math.max(0.8, Math.min(ZOOM_MAX_MOBILE, maxZoomForHeight))
  }, [isDesktop, contentHeight])

  // Scroll to current/first available node on mount
  useEffect(() => {
    if (!scrollContainerRef.current) return

    let targetY: number
    if (mapState.currentNodeId) {
      const currentNode = mapState.nodes.find(n => n.id === mapState.currentNodeId)
      if (currentNode) {
        targetY = currentNode.position.y * zoom - window.innerHeight / 2 + 28
      } else {
        targetY = contentHeight * zoom - window.innerHeight / 2
      }
    } else if (availableNodes.length > 0) {
      // Scroll to show first available nodes (bottom of map)
      targetY = contentHeight * zoom - window.innerHeight / 2
    } else {
      targetY = contentHeight * zoom - window.innerHeight / 2
    }

    scrollContainerRef.current.scrollTo({
      top: Math.max(0, targetY),
      behavior: "smooth",
    })
  }, [mapState.currentNodeId, availableNodes.length, contentHeight, zoom])

  // Handle node click
  const handleNodeClick = useCallback((node: MapNode) => {
    if (!isNodeAccessible(mapState, node.id)) return

    // If switching from a shop node to a different node, complete the shop node first
    if (shopEventReady && selectedNodeId && selectedNodeId !== node.id) {
      onCompleteShopNode?.()
      setShopEventReady(false)
    }

    // On mobile, show selection first, then tap again to confirm
    if (selectedNodeId === node.id) {
      // Second tap - confirm selection
      onNodeSelect(node)
      setSelectedNodeId(null)
    } else {
      // First tap - select node
      setSelectedNodeId(node.id)
    }
  }, [mapState, selectedNodeId, onNodeSelect, shopEventReady, onCompleteShopNode])

  // Handle backdrop click to deselect
  const handleBackdropClick = useCallback(() => {
    setSelectedNodeId(null)
    setShopEventReady(false)
  }, [])

  // Handle scroll wheel zoom
  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (!isDesktop) return
    e.preventDefault()

    const zoomSensitivity = 0.001
    const delta = -e.deltaY * zoomSensitivity
    const newZoom = Math.max(ZOOM_MIN, Math.min(maxZoom, zoom + delta))
    setZoom(newZoom)
  }, [isDesktop, zoom, maxZoom])

  // Handle touch start for pinch-to-zoom and drag-to-pan
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      // Pinch-to-zoom start
      const dx = e.touches[0].clientX - e.touches[1].clientX
      const dy = e.touches[0].clientY - e.touches[1].clientY
      pinchStartDistRef.current = Math.sqrt(dx * dx + dy * dy)
      pinchStartZoomRef.current = zoom
      // Stop any drag
      isDraggingRef.current = false
      dragStartRef.current = null
    } else if (e.touches.length === 1 && zoom > ZOOM_MIN) {
      // Drag-to-pan start (only when zoomed in)
      isDraggingRef.current = true
      dragStartRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
        scrollX: scrollContainerRef.current?.scrollLeft || 0,
        scrollY: scrollContainerRef.current?.scrollTop || 0,
      }
    }
  }, [zoom])

  // Handle touch move for pinch-to-zoom and drag-to-pan
  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      // Pinch-to-zoom
      const dx = e.touches[0].clientX - e.touches[1].clientX
      const dy = e.touches[0].clientY - e.touches[1].clientY
      const currentDist = Math.sqrt(dx * dx + dy * dy)

      if (pinchStartDistRef.current > 0) {
        const scale = currentDist / pinchStartDistRef.current
        const newZoom = Math.max(ZOOM_MIN, Math.min(maxZoom, pinchStartZoomRef.current * scale))
        setZoom(newZoom)
      }
    } else if (e.touches.length === 1 && isDraggingRef.current && dragStartRef.current && scrollContainerRef.current) {
      // Drag-to-pan (single touch)
      const dx = e.touches[0].clientX - dragStartRef.current.x
      const dy = e.touches[0].clientY - dragStartRef.current.y
      scrollContainerRef.current.scrollLeft = dragStartRef.current.scrollX - dx
      scrollContainerRef.current.scrollTop = dragStartRef.current.scrollY - dy
    }
  }, [maxZoom])

  // Handle touch end for drag-to-pan
  const handleTouchEnd = useCallback(() => {
    isDraggingRef.current = false
    dragStartRef.current = null
  }, [])

  // Handle mouse down for drag-to-pan (desktop)
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    // Only enable drag-to-pan when zoomed in (not at minimum zoom)
    if (zoom <= ZOOM_MIN) return

    isDraggingRef.current = true
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      scrollX: scrollContainerRef.current?.scrollLeft || 0,
      scrollY: scrollContainerRef.current?.scrollTop || 0,
    }
  }, [zoom])

  // Handle mouse move for drag-to-pan (desktop)
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDraggingRef.current || !dragStartRef.current || !scrollContainerRef.current) return

    const dx = e.clientX - dragStartRef.current.x
    const dy = e.clientY - dragStartRef.current.y
    scrollContainerRef.current.scrollLeft = dragStartRef.current.scrollX - dx
    scrollContainerRef.current.scrollTop = dragStartRef.current.scrollY - dy
  }, [])

  // Handle mouse up for drag-to-pan (desktop)
  const handleMouseUp = useCallback(() => {
    isDraggingRef.current = false
    dragStartRef.current = null
  }, [])

  // Handle escape key to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (selectedNodeId) {
          setSelectedNodeId(null)
        } else {
          onClose()
        }
      }
      if (e.key.toLowerCase() === "l") {
        onClose()
      }
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [selectedNodeId, onClose])

  // Get info for selected node
  const selectedNode = selectedNodeId ? mapState.nodes.find(n => n.id === selectedNodeId) : null
  const selectedLevel = selectedNode?.levelIndex !== null && selectedNode?.levelIndex !== undefined
    ? levels[selectedNode.levelIndex]
    : null

  // Get combat info for selected level (memoized)
  const selectedLevelCombatInfo = useMemo(() => {
    if (!selectedLevel || selectedNode?.type === "event") return null
    return getLevelCombatInfo(selectedLevel)
  }, [selectedLevel, selectedNode?.type])

  // Animate spaceship travel to a node
  const animateSpaceshipTo = useCallback((
    targetNode: MapNode,
    onComplete: () => void
  ) => {
    if (!currentNode || !spaceshipPosition) {
      onComplete()
      return
    }

    const startX = spaceshipPosition.x
    const startY = spaceshipPosition.y
    const endX = targetNode.position.x
    const endY = targetNode.position.y
    const startTime = performance.now()

    setIsSpaceshipTraveling(true)
    setIsSpaceshipOrbiting(false)

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime
      const progress = Math.min(elapsed / TRAVEL_DURATION, 1)

      // Ease out cubic for smooth deceleration
      const eased = 1 - Math.pow(1 - progress, 3)

      const x = startX + (endX - startX) * eased
      const y = startY + (endY - startY) * eased

      setSpaceshipPosition({ x, y })

      if (progress < 1) {
        travelAnimationRef.current = requestAnimationFrame(animate)
      } else {
        // Animation complete - start orbiting at new position
        setSpaceshipPosition({ x: endX, y: endY })
        setIsSpaceshipTraveling(false)
        setIsSpaceshipOrbiting(true)
        onComplete()
      }
    }

    travelAnimationRef.current = requestAnimationFrame(animate)
  }, [currentNode, spaceshipPosition])

  // Handle travel button click
  const handleTravel = useCallback(() => {
    if (!selectedNode || isSpaceshipTraveling) return

    // Play spaceship engine sound when starting travel
    playSpaceshipEngineSound()

    // For event nodes, handle reveal/collection flow
    if (selectedNode.type === "event") {
      // If not yet revealed, reveal it first
      if (!selectedNode.eventRevealed) {
        const result = onRevealEvent(selectedNode.id)
        if (result.revealed && result.eventType === "pelf" && result.itemId) {
          // Pelf event - animate spaceship then collect the reward
          animateSpaceshipTo(selectedNode, () => {
            // Update current node so spaceship orbits at destination
            onNodeArrival?.(selectedNode)
            const collectResult = onCollectPelf(selectedNode.id)
            setPelfRewardState({
              nodeId: selectedNode.id,
              itemId: result.itemId!,
              collected: collectResult.added,
              inventoryFull: collectResult.overflow,
            })
          })
          // Keep selected so user can see the reward
          return
        } else if (result.revealed && result.eventType === "shop") {
          // Shop event - animate spaceship, then show "Visit Shop" button
          animateSpaceshipTo(selectedNode, () => {
            // Update current node so spaceship orbits at destination
            onNodeArrival?.(selectedNode)
            // Set shop ready state instead of immediately opening shop
            setShopEventReady(true)
          })
          return
        }
      } else {
        // Already revealed
        if (selectedNode.eventType === "pelf") {
          // Pelf already collected, just close
          setSelectedNodeId(null)
          setPelfRewardState(null)
          return
        }
        // Shop already revealed - if we're already at this node OR shopEventReady, open the shop
        // Don't clear selectedNodeId or shopEventReady so user can reopen the shop
        if (shopEventReady || mapState.currentNodeId === selectedNode.id) {
          setShopEventReady(true) // Ensure it's set for future clicks
          onNodeSelect(selectedNode)
          return
        }
        // Shop revealed but not at location - animate then show Visit Shop
        animateSpaceshipTo(selectedNode, () => {
          onNodeArrival?.(selectedNode)
          setShopEventReady(true)
        })
        return
      }
    }

    // For combat/boss nodes, animate spaceship, then wait 3 seconds before starting
    animateSpaceshipTo(selectedNode, () => {
      // Immediately update current node so spaceship orbits at destination
      onNodeArrival?.(selectedNode)

      // Start countdown with progress animation
      setIsWaitingToStart(true)
      setLaunchProgress(0)

      // Animate launch progress from 0 to 100 over COMBAT_START_DELAY
      const startTime = performance.now()
      const animateProgress = (currentTime: number) => {
        const elapsed = currentTime - startTime
        const progress = Math.min(elapsed / COMBAT_START_DELAY, 1)
        setLaunchProgress(progress * 100)
        if (progress < 1) {
          launchAnimationRef.current = requestAnimationFrame(animateProgress)
        }
      }
      launchAnimationRef.current = requestAnimationFrame(animateProgress)

      // Store the pending node selection
      const timeout = setTimeout(() => {
        onNodeSelect(selectedNode)
        setSelectedNodeId(null)
        setIsWaitingToStart(false)
        setLaunchProgress(0)
        if (launchAnimationRef.current) {
          cancelAnimationFrame(launchAnimationRef.current)
        }
        pendingNodeSelectRef.current = null
      }, COMBAT_START_DELAY)

      pendingNodeSelectRef.current = { node: selectedNode, timeout }
    })
  }, [selectedNode, isSpaceshipTraveling, onNodeSelect, onNodeArrival, onRevealEvent, onCollectPelf, animateSpaceshipTo, shopEventReady])

  // Handle continue button after collecting pelf reward
  const handlePelfContinue = useCallback(() => {
    if (pelfRewardState) {
      // Mark the node as completed and continue
      setPelfRewardState(null)
      setSelectedNodeId(null)
    }
  }, [pelfRewardState])

  // Desktop sidebar content component (no longer shows travel button - moved to bottom bar)
  const SidebarContent = () => {
    if (selectedNode) {
      // Show selected node info (button is now in bottom bar)
      return (
        <div className="p-4 flex flex-col h-full">
          <h3 className="text-cyan-400 font-bold text-lg mb-4">Selected Sector</h3>

          <div className="flex items-center gap-2 mb-2">
            <span
              className={cn(
                "w-3 h-3 rounded-full",
                selectedNode.type === "boss" ? "bg-red-500" :
                selectedNode.type === "event" ? "bg-purple-500" :
                "bg-cyan-500"
              )}
            />
            <span className="text-white font-bold">
              {selectedNode.type === "event" ? "Trading Post" :
               selectedNode.type === "boss" ? "Stronghold" :
               selectedLevel?.name || "Sector"}
            </span>
          </div>

          <span className="text-gray-400 text-sm mb-2">
            {selectedNode.type === "boss" ? "Boss Battle" :
             selectedNode.type === "event" ? "Shop" :
             "Standard Combat"}
          </span>

          {selectedNode.type !== "event" && (
            <p className="text-sm mb-4">
              <span className="text-gray-500">Difficulty: </span>
              <span className={getDifficultyInfo(selectedNode.row, mapState.totalRows, selectedNode.type).color}>
                {getDifficultyInfo(selectedNode.row, mapState.totalRows, selectedNode.type).label}
              </span>
            </p>
          )}

          <p className="text-gray-500 text-sm mt-4">Use the button below to travel</p>
        </div>
      )
    }

    // No selection - show useful info
    return (
      <div className="p-4">
        <h3 className="text-cyan-400 font-bold text-lg mb-6">Star Chart</h3>

        {/* Legend */}
        <div className="mb-6">
          <h4 className="text-sm text-gray-400 mb-3 uppercase tracking-wide">Node Types</h4>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-cyan-500" />
              <span className="text-gray-300">Sector</span>
              <span className="text-gray-500 text-xs ml-auto">Combat</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-red-500" />
              <span className="text-gray-300">Stronghold</span>
              <span className="text-gray-500 text-xs ml-auto">Boss</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-purple-500" />
              <span className="text-gray-300">Trading Post</span>
              <span className="text-gray-500 text-xs ml-auto">Shop</span>
            </div>
          </div>
        </div>

        {/* Available paths */}
        <div className="mb-6">
          <h4 className="text-sm text-gray-400 mb-2 uppercase tracking-wide">Navigation</h4>
          <p className="text-cyan-300 text-lg font-bold">{availableNodes.length} sector{availableNodes.length !== 1 ? "s" : ""} accessible</p>
          <p className="text-gray-500 text-sm mt-1">Click a glowing node to select</p>
        </div>

        {/* Instructions */}
        <div className="border-t border-gray-700 pt-4 mt-auto">
          <h4 className="text-sm text-gray-400 mb-2 uppercase tracking-wide">Controls</h4>
          <div className="text-sm text-gray-500 space-y-1">
            <p>Click node → Select</p>
            <p>Click again → Travel</p>
            <p>Scroll wheel → Zoom</p>
            <p>Drag → Pan (when zoomed)</p>
            <p>Press <kbd className="bg-gray-800 px-1 rounded">Esc</kbd> to close</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-[100] flex bg-[#0a0a1a]">
      {/* Main map area */}
      <div className={cn(
        "flex-1 flex flex-col",
        isDesktop && "mr-[320px]"
      )}>
        {/* Scrollable map area */}
        <div
          ref={scrollContainerRef}
          className={cn(
            "flex-1 overflow-auto relative star-chart-scrollbar",
            zoom > ZOOM_MIN && "cursor-grab active:cursor-grabbing"
          )}
          onClick={handleBackdropClick}
          onWheel={handleWheel}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          style={{ touchAction: zoom > ZOOM_MIN ? "none" : "pan-x pan-y pinch-zoom" }}
        >
          {/* Star background image */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage: "url('/images/starmap/starmap.png')",
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              minHeight: contentHeight * zoom,
              minWidth: 360 * zoom,
              imageRendering: "pixelated",
            }}
          />

          {/* Map content with zoom transform */}
          <div
            ref={mapContentRef}
            className="relative mx-auto"
            style={{
              width: 360 * zoom,
              height: contentHeight * zoom,
              transformOrigin: "top center",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Connections layer (SVG) - positioned behind nodes */}
            <div style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", zIndex: 1 }}>
              <SpaceMapConnections
                edges={mapState.edges}
                nodes={mapState.nodes}
                visitedNodeIds={mapState.visitedNodeIds}
                currentNodeId={mapState.currentNodeId}
                availableNodeIds={availableNodeIds}
                height={contentHeight}
                zoom={zoom}
              />
            </div>

            {/* Nodes layer - positioned in front of connections */}
            <div style={{ position: "relative", zIndex: 2 }}>
              {mapState.nodes.map((node) => (
                <SpaceMapNode
                  key={node.id}
                  node={node}
                  isAvailable={availableNodeIds.includes(node.id)}
                  isSelected={selectedNodeId === node.id}
                  isCurrent={node.id === mapState.currentNodeId}
                  isVisited={mapState.visitedNodeIds.includes(node.id)}
                  onClick={() => handleNodeClick(node)}
                  zoom={zoom}
                />
              ))}
            </div>

            {/* Spaceship layer - positioned above everything */}
            {spaceshipPosition && (
              <div
                className="absolute pointer-events-none"
                style={{
                  left: spaceshipPosition.x * zoom,
                  top: spaceshipPosition.y * zoom,
                  zIndex: 10,
                  transform: "translate(-50%, -50%)",
                }}
              >
                <div
                  className={isSpaceshipOrbiting && !isSpaceshipTraveling ? "animate-orbit" : ""}
                  style={{ "--orbit-radius": `${45 * zoom}px` } as React.CSSProperties}
                >
                  <img
                    src="/images/starmap/starmap_carrier.png"
                    alt="carrier"
                    style={{
                      width: 56 * 0.525 * 0.9 * zoom,
                      height: "auto",
                      imageRendering: "pixelated",
                      filter: "drop-shadow(0 0 4px rgba(34, 211, 238, 0.8)) drop-shadow(0 0 8px rgba(34, 211, 238, 0.6))",
                      transform: "rotate(180deg)",
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Bottom bar with travel button - appears when node is selected (both mobile and desktop) */}
        {selectedNode && (
          <div className="bg-black/95 border-t border-cyan-500/50 p-3">
            {/* Pelf Reward Display */}
            {pelfRewardState && pelfRewardState.itemId && itemDefinitions[pelfRewardState.itemId] && (
              <div className="flex flex-col items-center gap-3 mb-3">
                <h3 className="text-amber-400 font-bold text-lg">Pelf Found!</h3>
                <div className="w-full max-w-xs">
                  <ItemRewardCard
                    itemDef={itemDefinitions[pelfRewardState.itemId]}
                    showParticles={true}
                    message={pelfRewardState.inventoryFull
                      ? "Inventory full - will drop on arrival"
                      : "Added to inventory"}
                  />
                </div>
                <button
                  onClick={handlePelfContinue}
                  className="px-8 py-2.5 bg-amber-600 hover:bg-amber-500 text-white font-bold rounded-lg transition-colors"
                >
                  Continue
                </button>
              </div>
            )}

            {/* Normal node info - hide when showing pelf reward */}
            {!pelfRewardState && (
              <div className="flex flex-col items-center gap-3">
                {/* Node info row */}
                <div className="flex items-center justify-center gap-3 w-full">
                  <span
                    className={cn(
                      "w-3 h-3 rounded-full shrink-0",
                      selectedNode.type === "boss" ? "bg-red-500" :
                      selectedNode.type === "event" ? "bg-purple-500" :
                      "bg-cyan-500"
                    )}
                  />
                  <span className="text-white font-bold">
                    {selectedNode.type === "event"
                      ? (selectedNode.eventType === "shop"
                          ? "Trading Post"
                          : (selectedNode.eventRevealed ? "Salvage Cache" : "Unknown Signal"))
                      : selectedNode.type === "boss" ? "Stronghold"
                      : selectedLevel?.name || "Sector"}
                  </span>
                  <span className="text-gray-500 text-sm">
                    {selectedNode.type === "boss" ? "Boss" :
                     selectedNode.type === "event"
                       ? (selectedNode.eventType === "shop"
                           ? "Shop"
                           : (selectedNode.eventRevealed ? "Loot" : "???"))
                       : "Combat"}
                  </span>
                </div>

                {/* Combat info row - only for combat/boss nodes */}
                {selectedLevelCombatInfo && (
                  <div className="flex items-center justify-center gap-4 text-xs">
                    <div className="flex items-center gap-1.5">
                      <span className="text-gray-500">Armor:</span>
                      <span className={armorInfo[selectedLevelCombatInfo.dominantArmor].color}>
                        {armorInfo[selectedLevelCombatInfo.dominantArmor].label}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-gray-500">Damage:</span>
                      <span className={damageInfo[selectedLevelCombatInfo.dominantDamage].color}>
                        {damageInfo[selectedLevelCombatInfo.dominantDamage].label}
                      </span>
                    </div>
                  </div>
                )}

                {/* Travel button with ping effect - centered */}
                <div className="relative">
                  {/* Ping animation rings - only show when not traveling */}
                  {!isSpaceshipTraveling && !isWaitingToStart && (
                    <>
                      <span className="absolute inset-0 rounded-lg bg-cyan-400 animate-ping opacity-30" />
                      <span className="absolute inset-0 rounded-lg bg-cyan-400 animate-ping opacity-20" style={{ animationDelay: "0.5s" }} />
                    </>
                  )}
                  <button
                    onClick={handleTravel}
                    disabled={isSpaceshipTraveling || isWaitingToStart}
                    className={cn(
                      "relative px-8 py-2.5 font-bold rounded-lg transition-colors overflow-hidden",
                      isSpaceshipTraveling || isWaitingToStart
                        ? "bg-gray-700 text-white cursor-not-allowed"
                        : "bg-cyan-600 hover:bg-cyan-500 text-white"
                    )}
                  >
                    {/* Launch progress fill animation */}
                    {isWaitingToStart && (
                      <div
                        className="absolute inset-0 bg-cyan-600 transition-none"
                        style={{ width: `${launchProgress}%` }}
                      />
                    )}
                    <span className="relative z-10">
                      {isSpaceshipTraveling
                        ? "Traveling..."
                        : isWaitingToStart
                          ? "Launching pod..."
                          : selectedNode.type === "event"
                            ? (selectedNode.eventType === "shop"
                                ? "Visit Shop"
                                : (shopEventReady || selectedNode.eventRevealed
                                    ? "Collect"
                                    : "Investigate"))
                            : "Travel"}
                    </span>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Legend bar when no node selected (mobile only) */}
        {!isDesktop && !selectedNode && (
          <div className="bg-black/50 border-t border-gray-700/50 px-4 py-2 flex items-center justify-center">
            <div className="flex items-center gap-4 text-xs text-gray-400">
              <div className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-cyan-500" />
                <span>Sector</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-red-500" />
                <span>Boss</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-purple-500" />
                <span>Shop</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Upgrade Tree Button - Fixed position, left of sidebar on desktop */}
      {onOpenUpgradeTree && (
        <div className={`fixed top-4 z-110 ${isDesktop ? "right-84" : "right-4"}`}>
          <button
            onClick={onOpenUpgradeTree}
            className="relative flex items-center gap-2 px-4 py-2.5 bg-cyan-600 hover:bg-cyan-500 border border-cyan-400/50 rounded-lg text-white text-sm font-bold transition-colors backdrop-blur-sm"
          >
            <BookUp size={18} />
            <span>Upgrades</span>
          </button>
        </div>
      )}

      {/* Desktop: Right sidebar (always visible) */}
      {isDesktop && (
        <div className="fixed right-0 top-0 bottom-0 w-[320px] bg-black/90 border-l border-cyan-500/50 flex flex-col">
          <SidebarContent />
        </div>
      )}
    </div>
  )
}
